import { type NoticeStatus } from "@/hooks/use-notices";

/**
 * The URL axes a drill arrives on: a ladder's status rail sending the officer
 * to the letters it is waiting on (`ladder_id`), or a deep link naming a
 * letter kind and a generated-at window directly (`letter_kind`,
 * `generated_from`, `generated_to`). A drill must reproduce the surface it
 * came from, so it restates that question's whole query rather than
 * inheriting the tab's defaults. Each is surfaced as a removable chip, so the
 * officer can always see and undo what a drill narrowed the list to — and
 * each OUTRANKS the equivalent filter-bar control while it is present.
 */
export interface NoticeDrill {
  /** Starter-library family identity, e.g. `cc6_shut_off`. */
  letter_kind?: string;
  /** Explicit status set — OVERRIDES the tab's own set when present. */
  status?: NoticeStatus[];
  /** ISO timestamp, inclusive lower bound on generated_at. */
  generated_from?: string;
  /** ISO timestamp, EXCLUSIVE upper bound on generated_at. */
  generated_to?: string;
  /** One escalation ladder's letters — the ladder builder's queue link. */
  ladder_id?: string;
}

const NOTICE_STATUSES: readonly NoticeStatus[] = [
  "draft",
  "approved",
  "sending",
  "sent",
  "delivered",
  "failed",
  "rejected",
  "cancelled",
  "print_ready",
  "hand_delivered",
  "imported",
];

/** Keep only recognized statuses; an empty result reads as "no override". */
export function parseDrillStatusSet(value: unknown): NoticeStatus[] | undefined {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  const parsed = raw.filter((v): v is NoticeStatus =>
    (NOTICE_STATUSES as readonly unknown[]).includes(v),
  );
  return parsed.length > 0 ? parsed : undefined;
}

/** A drill is active when ANY axis is present — it then counts as a filter. */
export function hasDrillAxes(drill: NoticeDrill): boolean {
  return Object.values(drill).some((value) => value !== undefined);
}

/** Render a window bound as a plain date; an open end shows as an em dash. */
export function formatDrillBound(iso: string | undefined): string {
  if (!iso) return "—";
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? iso
    : at.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
